import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPrint, FaEye } from 'react-icons/fa'
import { MdClose } from 'react-icons/md'
import CustomButton from '../../shared/CustomButton'
import ReusableModal from '../../shared/ReuseableModal'
import { handlePrintData } from '../../utils/PrintData'
import SkeletonCardLoading from '../../shared/SkeltonCardLoading'
import toast from 'react-hot-toast'
import LabReportPrint from './LabReportPrint'
import RadiologyReportPrint from './RadiologyReportPrint'
import { fetchData } from '../../helpers/fetchData'

const ViewOrderResultsModal = ({
    visible,
    setVisible,
    orderNo,
    patient,
    practitioner,
    radiology = false
}) => {
    const { t } = useTranslation()
    const printRef = useRef(null)
    const [loading, setLoading] = useState(false)
    const [resultsData, setResultsData] = useState([])

    useEffect(() => {
        if (visible && orderNo) {
            fetchOrderResults()
        }
    }, [visible, orderNo])

    const fetchOrderResults = async () => {
        setLoading(true)
        try {
            const endpoint = radiology
                ? `/v3/radiology/getResultByOrderNo`
                : `/v3/lab/getResultByOrderNo`
            const payload = {}
            if (radiology) {
                payload.order_id = orderNo
            } else {
                payload.order_no = orderNo
                payload.result_type = 'INTERNAL'
            }

            const response = await fetchData('POST', endpoint, payload)

            if (response?.status === 200) {
                const responseData = radiology
                    ? response?.data
                    : response?.data?.result
                    ? {
                        ...response.data.result,
                        settings: response.data.settings || response.data.result.settings,
                    }
                    : response?.data

                if (responseData?.orders && Array.isArray(responseData.orders)) {
                    const matchingOrder = responseData.orders.find(order => order.order_number === orderNo)
                    if (matchingOrder) {
                        setResultsData(matchingOrder)
                    } else {
                        setResultsData([])
                    }
                } 
                else if (radiology && responseData?.order_items) {
                    const items = responseData.order_items.map(item => {
                        const history = [];
                        if (item.received_info?.at) {
                            history.push({
                                action_performers: {
                                    id: item.received_info.by_id || 0,
                                    name: item.received_info.by_name || "",
                                    role_id: item.received_info.by_role_id || 0,
                                    role_name: "",
                                    time: item.received_info.at
                                },
                                action_reason: "Order Item Received"
                            });
                        }
                        if (item.approved_info?.at) {
                            history.push({
                                action_performers: {
                                    id: item.approved_info.by_id || 0,
                                    name: item.approved_info.by_name || "",
                                    role_id: item.approved_info.by_role_id || 0,
                                    role_name: "",
                                    time: item.approved_info.at
                                },
                                action_reason: "Order Item Result Uploaded"
                            });
                        }
                        return {
                            history: history,
                            item_id: item.id || item._id,
                            item_name: item.non_standard_desc || item.service_desc,
                            result_upload: item.result_upload
                        };
                    });
                    const transformedData = {
                        items: items,
                        order_number: responseData.order_no,
                        patient_info: responseData.patient,
                        practitioner_info: responseData.practitioner
                    }
                    setResultsData([transformedData])
                }
                else if (radiology && responseData) {
                    setResultsData([responseData])
                } 
                else if (Array.isArray(responseData)) {
                    setResultsData(responseData)
                } 
                else if (responseData) {
                    setResultsData([responseData])
                } else {
                    setResultsData([])
                }
            }
        } catch (error) {
            console.error('Error fetching order results:', error)
        } finally {
            setLoading(false)
        }
    }

    const handlePrint = async () => {
        if (printRef.current) {
            await handlePrintData(
                printRef.current.innerHTML,
                `${radiology ? 'Radiology' : 'Lab'}_Order_Report_${orderNo || ''}`
            )
        }
    }

    const handleClose = () => {
        setVisible(false)
        setResultsData([])
    }
    console.log(resultsData);

    return (
        <ReusableModal
            isVisible={visible}
            onClose={handleClose}
            title={t(radiology ? 'View Radiology Order Results' : 'View Lab Order Results')}
            HeaderIcon={FaEye}
            className='w-[90%] md:w-[80%] lg:w-[70%]'
            position='top'
            footerButtons={
                <div className="flex items-center gap-2">
                    <CustomButton
                        label={t('Print All Reports')}
                        icon={<FaPrint />}
                        onClick={handlePrint}
                        className='h-[32px]'
                        disabled={loading || resultsData.length === 0}
                    />
                </div>
            }
        >
            <div className="print-container max-h-[80vh]">
                {loading ? (
                    <SkeletonCardLoading count={5} />
                ) : resultsData?.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                        <p>{t('No results available for this order')}</p>
                    </div>
                ) : (
                    <div className="print-content" ref={printRef}>
                        <div className="space-y-4">
                            {radiology ? (
                                resultsData?.map((orderData, index) => {
                                    return (
                                        <div key={orderData._id || index} className="border-b pb-6 last:border-b-0">
                                            <RadiologyReportPrint
                                                selectedItem={null}
                                                orderData={orderData}
                                                selectedItemId="all"
                                            />
                                            {index < resultsData.length - 1 && (
                                                <div className="mt-4 border-t-2 border-dashed border-gray-300" />
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <LabReportPrint
                                    reportData={resultsData}
                                    patient={patient}
                                    practitioner={practitioner}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ReusableModal>
    )
}

export default ViewOrderResultsModal
